import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, BarChart3, CreditCard, Map,
  Sparkles, Check, Building2, Receipt, FileText, AlertTriangle
} from "lucide-react";

const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID;
const TEST_MODE = process.env.REACT_APP_PRICING_TEST_MODE === "true" || !RAZORPAY_KEY;

const PRO_FEATURES = [
  "Unlimited meetings",
  "Unlimited data sources",
  "Live AI Wingman cards",
  "Meeting prep & review",
  "Google Calendar sync",
  "Priority support",
];

const PRO_ROADMAP = [
  { label: "Meeting Memory", desc: "Remembers people, topics & commitments across calls" },
  { label: "Reasoning", desc: "Goal-oriented Reasoning" },
  { label: "Meeting Re-join", desc: "Rejoin ended meetings without losing context or notes" },
];

const ENTERPRISE_ROADMAP = [
  { label: "Integrations", desc: "Gmail, Slack, Salesforce & HubSpot — pull context in, push notes out" },
  { label: "Security & Compliance", desc: "SSO/SAML, RBAC, & audit logs" },
  { label: "Team Collaboration", desc: "Shared meeting insights, team dashboards & admin controls" },
  { label: "Post-Meeting Automation", desc: "Auto-send summaries, create tasks & update CRM records" },
];

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (document.getElementById("razorpay-checkout")) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.id = "razorpay-checkout";
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

function ProgressBar({ used, limit, label }) {
  const isUnlimited = limit === -1;
  const pct = isUnlimited ? 0 : Math.min((used / limit) * 100, 100);
  const color =
    isUnlimited ? "bg-green-500"
      : pct >= 100 ? "bg-red-500"
        : pct >= 70 ? "bg-amber-500"
          : "bg-green-500";

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <span className="text-sm text-gray-500">
          {isUnlimited ? (
            <span className="text-green-600 font-medium">Unlimited</span>
          ) : (
            <>{used}/{limit}</>
          )}
        </span>
      </div>
      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${color}`}
          style={{ width: isUnlimited ? "100%" : `${pct}%` }}
        />
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [usage, setUsage] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usageResp, subResp] = await Promise.all([
          axios.get(`${API}/pricing/usage`, { withCredentials: true }),
          axios.get(`${API}/pricing/subscription`, { withCredentials: true }),
        ]);
        setUsage(usageResp.data);
        setSubscription(subResp.data);
      } catch (err) {
        console.error("Failed to fetch settings data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const isPro = user?.plan_tier === "pro";

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const { data } = await axios.post(`${API}/pricing/cancel`, {}, { withCredentials: true });
      setSubscription((prev) => prev ? { ...prev, status: "cancelling", cancel_at: data.cancel_at } : prev);
      setShowCancelConfirm(false);
    } catch (err) {
      console.error("Failed to cancel subscription:", err);
      const msg = err.response?.data?.detail || "Failed to cancel subscription.";
      toast.error(typeof msg === "string" ? msg : msg.message || "Something went wrong.");
    } finally {
      setCancelling(false);
    }
  };

  const handleSubscribe = async () => {
    setSubscribing(true);
    try {
      const { data } = await axios.post(`${API}/pricing/subscribe`, {}, { withCredentials: true });

      if (TEST_MODE) {
        await axios.post(
          `${API}/pricing/verify`,
          {
            razorpay_payment_id: "test_pay_" + Date.now(),
            razorpay_subscription_id: data.razorpay_subscription_id,
            razorpay_signature: "test_sig_" + Date.now(),
          },
          { withCredentials: true }
        );
        await checkAuth();
        toast.success("Upgraded to Pro! (Test Mode)");
        window.location.reload();
        return;
      }

      const loaded = await loadRazorpayScript();
      if (!loaded) {
        toast.error("Failed to load payment gateway. Please try again.");
        setSubscribing(false);
        return;
      }

      const options = {
        key: RAZORPAY_KEY,
        subscription_id: data.razorpay_subscription_id,
        name: "ShadowLabs",
        description: "Pro Plan - Monthly",
        handler: async (response) => {
          try {
            await axios.post(
              `${API}/pricing/verify`,
              {
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_subscription_id: response.razorpay_subscription_id,
                razorpay_signature: response.razorpay_signature,
              },
              { withCredentials: true }
            );
            await checkAuth();
            toast.success("Welcome to Pro! Your account has been upgraded.");
            window.location.reload();
          } catch {
            toast.error("Payment verification failed. Please contact support.");
          }
        },
        prefill: { email: user?.email, name: user?.name },
        theme: { color: "#0f172a" },
        modal: { ondismiss: () => setSubscribing(false) },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", () => setSubscribing(false));
      rzp.open();
    } catch (err) {
      setSubscribing(false);
      const msg = err.response?.data?.detail || "Failed to start subscription.";
      toast.error(typeof msg === "string" ? msg : msg.message || "Something went wrong.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gray-300 border-t-gray-700 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="mb-8">
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <div className="flex items-center gap-2.5">
            <CreditCard className="w-6 h-6 text-gray-700" />
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
              Billing & Invoice
            </h1>
          </div>
        </div>

        {/* ── Usage ── */}
        <section className="bg-white rounded-2xl border border-gray-200 p-6 sm:p-8 mb-6">
          <div className="flex items-center gap-2 mb-6">
            <BarChart3 className="w-5 h-5 text-gray-700" />
            <h2 className="text-lg font-semibold text-gray-900">Usage</h2>
            {isPro && (
              <span className="bg-gray-900 text-white text-xs font-medium px-2 py-0.5 rounded-full ml-auto">
                Pro
              </span>
            )}
          </div>
          <div className="space-y-5">
            {usage && (
              <>
                <ProgressBar used={usage.meetings_used} limit={usage.meetings_limit} label="Meetings this month" />
                <ProgressBar used={usage.sources_used} limit={usage.sources_limit} label="Data sources" />
              </>
            )}
          </div>
        </section>

        {/* ── Plan & Billing ── */}
        <section className="bg-white rounded-2xl border border-gray-200 p-6 sm:p-8 mb-6">
          <div className="flex items-center gap-2 mb-6">
            <CreditCard className="w-5 h-5 text-gray-700" />
            <h2 className="text-lg font-semibold text-gray-900">Plan</h2>
          </div>

          <div className="flex items-center justify-between py-3 border-b border-gray-100">
            <span className="text-sm text-gray-500">Current plan</span>
            <span className="text-sm font-medium text-gray-900">{isPro ? "Pro" : "Free"}</span>
          </div>

          {/* ─ Pro user: subscription details + cancel ─ */}
          {isPro && subscription && (
            <>
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <span className="text-sm text-gray-500">Status</span>
                {subscription.status === "cancelling" ? (
                  <span className="text-sm font-medium text-amber-600">
                    Cancels {subscription.cancel_at
                      ? new Date(subscription.cancel_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                      : "at end of billing period"}
                  </span>
                ) : subscription.status === "past_due" ? (
                  <span className="text-sm font-medium text-red-600">Payment failed — retrying</span>
                ) : (
                  <span className="text-sm font-medium text-green-600 capitalize">{subscription.status}</span>
                )}
              </div>

              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <span className="text-sm text-gray-500">Amount</span>
                <span className="text-sm font-medium text-gray-900">
                  ${subscription.amount ? (subscription.amount / 100).toFixed(0) : "59"}/month
                </span>
              </div>

              {subscription.status === "cancelling" ? (
                <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-2.5">
                    <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">Subscription scheduled to cancel</p>
                      <p className="text-xs text-amber-600 mt-1">
                        Pro access continues until{" "}
                        {subscription.cancel_at
                          ? new Date(subscription.cancel_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
                          : "the end of your billing period"}
                        . After that your plan reverts to Free.
                      </p>
                    </div>
                  </div>
                </div>
              ) : subscription.status === "past_due" ? (
                <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start gap-2.5">
                    <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-red-800">Payment failed</p>
                      <p className="text-xs text-red-600 mt-1">
                        We couldn't charge your card. Razorpay will retry automatically. Please update your payment method if the issue persists.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mt-4">
                  {!showCancelConfirm ? (
                    <button
                      onClick={() => setShowCancelConfirm(true)}
                      className="text-sm text-gray-400 hover:text-red-500 transition-colors"
                    >
                      Cancel subscription
                    </button>
                  ) : (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-start gap-2.5 mb-3">
                        <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-red-800">Cancel your Pro subscription?</p>
                          <p className="text-xs text-red-600 mt-1">
                            You'll keep Pro access until the end of your current billing period, then revert to Free.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 pl-7">
                        <button
                          onClick={handleCancel}
                          disabled={cancelling}
                          className="text-sm font-medium text-white bg-red-600 hover:bg-red-700 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                        >
                          {cancelling ? "Cancelling..." : "Yes, cancel"}
                        </button>
                        <button
                          onClick={() => setShowCancelConfirm(false)}
                          className="text-sm font-medium text-gray-600 hover:text-gray-900 px-3 py-1.5 transition-colors"
                        >
                          Keep Pro
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* ─ Free user: upgrade card ─ */}
          {!isPro && (
            <div className="mt-5">
              <div className="border-2 border-gray-900 rounded-xl p-5 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
                  <div>
                    <div className="flex items-center gap-2 mb-1.5">
                      <Sparkles className="w-4 h-4 text-gray-900" />
                      <h4 className="text-base font-semibold text-gray-900">Upgrade to Pro</h4>
                    </div>
                    <p className="text-xs text-gray-500">Unlimited meetings and data sources.</p>
                  </div>
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-3xl font-bold text-gray-900">$59</span>
                    <span className="text-gray-500 text-xs">/mo</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 mb-5">
                  {PRO_FEATURES.map((f) => (
                    <div key={f} className="flex items-center gap-1.5 text-xs text-gray-700">
                      <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                      {f}
                    </div>
                  ))}
                </div>

                <button
                  onClick={handleSubscribe}
                  disabled={subscribing}
                  className="w-full sm:w-auto px-6 py-2.5 rounded-lg text-sm font-medium bg-gray-900 text-white hover:bg-gray-800 transition-colors disabled:opacity-50"
                >
                  {subscribing ? "Processing..." : "Upgrade to Pro"}
                </button>
              </div>

              <div className="flex items-center justify-between mt-4 px-1">
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <Building2 className="w-3.5 h-3.5" />
                  <span><span className="font-medium">Enterprise</span> — SSO, teams, custom integrations</span>
                  <span className="bg-gray-100 text-gray-400 text-[10px] font-medium px-1.5 py-0.5 rounded-full">Soon</span>
                </div>
                <a
                  href="https://wa.me/919582819710?text=Hi%2C%20I%27m%20interested%20in%20Shadow%20Enterprise%20plan"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-gray-400 hover:text-gray-600 transition-colors whitespace-nowrap"
                >
                  Contact Us
                </a>
              </div>
            </div>
          )}
        </section>

        {/* ── Invoices ── */}
        <section className="bg-white rounded-2xl border border-gray-200 p-6 sm:p-8 mb-6">
          <div className="flex items-center gap-2 mb-6">
            <Receipt className="w-5 h-5 text-gray-700" />
            <h2 className="text-lg font-semibold text-gray-900">Invoices</h2>
          </div>

          {isPro && subscription?.razorpay_subscription_id ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <FileText className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Pro Plan — ${subscription.amount ? (subscription.amount / 100).toFixed(2) : "59.00"}/mo
                    </p>
                    <p className="text-xs text-gray-500">Current billing period</p>
                  </div>
                </div>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                  subscription.status === "past_due"
                    ? "text-red-600 bg-red-50"
                    : subscription.status === "cancelling"
                      ? "text-amber-600 bg-amber-50"
                      : "text-green-600 bg-green-50"
                }`}>
                  {subscription.status === "past_due" ? "Payment failed" : subscription.status === "cancelling" ? "Cancelling" : "Active"}
                </span>
              </div>
              <p className="text-xs text-gray-400 pt-1">Invoices are generated automatically each billing cycle.</p>
            </div>
          ) : (
            <div className="text-center py-6">
              <Receipt className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No invoices yet</p>
              <p className="text-xs text-gray-400 mt-1">Invoices will appear here once you subscribe to Pro.</p>
            </div>
          )}
        </section>

        {/* ── Roadmap ── */}
        <section className="bg-white rounded-2xl border border-gray-200 p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-4">
            <Map className="w-5 h-5 text-gray-700" />
            <h2 className="text-lg font-semibold text-gray-900">Roadmap</h2>
          </div>

          <p className="text-sm text-gray-600 mb-6">
            {isPro
              ? `Here's what's coming next. Your $${subscription?.amount ? (subscription.amount / 100).toFixed(0) : "59"}/month Pro plan includes these features.`
              : <>Pro is <span className="font-semibold">$59/month</span>. Upgrade to get unlimited meetings and sources.</>
            }
          </p>

          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-gray-900" />
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Coming to Pro</h3>
            </div>
            <ul className="space-y-3">
              {PRO_ROADMAP.map((item) => (
                <li key={item.label} className="flex items-start gap-2.5 text-sm">
                  <div className="w-1.5 h-1.5 rounded-full bg-gray-900 mt-1.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium text-gray-900">{item.label}</span>
                    <span className="text-gray-500"> — {item.desc}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="pt-5 border-t border-gray-100">
            <div className="flex items-center gap-2 mb-3">
              <Building2 className="w-4 h-4 text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Coming to Enterprise</h3>
            </div>
            <ul className="space-y-3">
              {ENTERPRISE_ROADMAP.map((item) => (
                <li key={item.label} className="flex items-start gap-2.5 text-sm">
                  <div className="w-1.5 h-1.5 rounded-full bg-gray-300 mt-1.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium text-gray-600">{item.label}</span>
                    <span className="text-gray-400"> — {item.desc}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
}
